import { forwardRef, HTMLAttributes, ReactElement, ReactNode, Ref } from 'react'
import { Box } from '@mui/material'
import MuiCard, { CardProps } from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardHeader from '@mui/material/CardHeader'
import Collapse from '@mui/material/Collapse'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import Skeleton from '@mui/material/Skeleton'
import { Casing, TEXT_ROLE_CASING } from '@common/helpers/casing'
import cn from 'classnames'
import { DEFAULT_EMPTY_OBJECT_PROP } from '../../../constants/common'
import { formatChildren } from '../Text'
import styles from './Card.module.scss'

interface IProps extends Omit<CardProps, 'title'> {
  title?: ReactNode
  /** Overrides the card title's case; `none` when the title is a record's name. */
  titleCasing?: Casing
  noPadding?: boolean
  noVerticalPadding?: boolean
  noHeaderMargin?: boolean
  noHeaderTopMargin?: boolean
  classes?: { [key: string]: string }
  renderHeaderLeftColumn?: () => ReactElement
  renderHeaderRightColumn?: () => ReactElement
  expandable?: boolean
  isExpanded?: boolean
  withBorder?: boolean
  withHeaderBorder?: boolean
  isLoading?: boolean
  headerProps?: HTMLAttributes<HTMLDivElement> & { [key: string]: unknown }
}

const Card = forwardRef(
  (
    {
      children,
      title,
      titleCasing = TEXT_ROLE_CASING.cardTitle,
      noPadding,
      noVerticalPadding,
      noHeaderMargin,
      noHeaderTopMargin,
      classes = DEFAULT_EMPTY_OBJECT_PROP,
      renderHeaderLeftColumn,
      renderHeaderRightColumn,
      expandable = false,
      isExpanded = false,
      withBorder = true,
      withHeaderBorder = false,
      isLoading,
      headerProps = DEFAULT_EMPTY_OBJECT_PROP,
      ...props
    }: IProps,
    ref: Ref<HTMLDivElement>,
  ) => {
    const sizeClasses = {
      [styles.noPadding]: noPadding,
      [styles.noVerticalPadding]: noVerticalPadding,
    }

    return (
      <MuiCard
        {...props}
        classes={{
          root: cn(styles.root, sizeClasses, {
            [styles.withBorder]: withBorder,
            [classes.root]: classes.root,
          }),
        }}
        ref={ref}
      >
        <Box
          {...headerProps}
          className={cn(styles.headerWrap, {
            [styles.noHeaderMargin]: noHeaderMargin,
            [styles.noHeaderTopMargin]: noHeaderTopMargin,
            [styles.withBorder]: withHeaderBorder,
          })}
        >
          <Box
            className={cn(styles.headerMain, {
              [styles.headerMainWithLeftColumn]: renderHeaderLeftColumn,
            })}
          >
            {title && (
              <CardHeader
                classes={{
                  root: cn(styles.headerRoot, sizeClasses),
                  title: cn(styles.title, sizeClasses, classes.title),
                }}
                title={
                  <Grid container>
                    <Grid size={12}>{formatChildren(titleCasing, title)}</Grid>
                    <Grid size={12}>
                      {withHeaderBorder && <Divider className={styles.divider} light />}
                    </Grid>
                  </Grid>
                }
              />
            )}
            {renderHeaderLeftColumn && renderHeaderLeftColumn()}
          </Box>
          {renderHeaderRightColumn && <Box>{renderHeaderRightColumn()}</Box>}
        </Box>
        {expandable && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <CardContent
              classes={{
                root: cn(styles.content, classes.content, sizeClasses),
              }}
            >
              {isLoading ? <Skeleton variant="rectangular" width="100%" height="100%" /> : children}
            </CardContent>
          </Collapse>
        )}
        {!expandable && (
          <CardContent
            classes={{
              root: cn(styles.content, classes.content, sizeClasses),
            }}
          >
            {isLoading ? <Skeleton variant="rectangular" width="100%" height={118} /> : children}
          </CardContent>
        )}
      </MuiCard>
    )
  },
)

export default Card
